<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref} from 'vue'
import {filterListType} from "@/assets/ts/types/filters";
import {FilterLimiter} from "@/assets/ts/FilterLimiter";
import {i18n} from "@/main";
import {useI18n} from "vue-i18n";
import CatalogMenu from "@/components/catalog/catalogMenu.vue";
import CatalogMenuSkeleton from "@/components/catalog/catalogMenuSkeleton.vue";
import CatalogResult from "@/components/catalog/catalogResult.vue";
import StepWidget from "@/components/stepWidget.vue";
import ElementVideo from "@/components/elements/elementVideo.vue";
import {Config} from "@/assets/ts/config";

interface Product {
  set_id: string;
  hash?: string;
  price: number;
  price_gold: number;
  price_platinum: number;
  metal: string;
  stones: string;
  image: {
    filename: string
    filename_layer: string
    positions: {
      filename: string
      filename_layer: string
    }[]
  };
  order_idx?: number;
}

const pageStr = "components.catalog"

const mobileFiltersOpen = ref(false)
const isMobile = ref(false)

const checkMobile = () => {
  isMobile.value = window.innerWidth < 1024;
};

const {t} = useI18n();

const filterLimiter = new FilterLimiter();

/**
 * catalog props
 */

let catalogBrand = Config.getCatalogBrand() || "R"
const catalogItemType = ref(Config.getCatalogItemType() || "engagement_ring")

const catalogItemTypes = [
  "wedding_ring",
  "engagement_ring",
  "engagement_ring_rings_of_the_stars",
  "eternity_ring",
  "earring",
  "necklace",
  "jewelry_sets",
  "tennis"
]

const mergedFilters = computed(() => {
  // If runtimeConfig catalogBrand is "R" remove the "wedding_ring" from catalogItemTypes
  let finalCatalogItemTypes = catalogItemTypes
  if (catalogBrand === "R") {
    finalCatalogItemTypes.splice(catalogItemTypes.indexOf("wedding_ring"), 1);
  }
  //

  const result: filterListType = {
    catalogItemType: {
      order: -1,
      prio: true,
      values: finalCatalogItemTypes,
      lowercaseImage: false
    },
    price_sort: {
      order: (filters.value['price']?.order || 2) + 0.1,
      prio: false,
      values: ['default', 'min-price', 'max-stone-size', 'max-quality'],
      lowercaseImage: false
    }
  }

  return {...result, ...filters.value}
})

const locale = (i18n.global.locale as any).value || "de-DE";

let [language, country] = locale.split("-");

const products = ref<Product[]>([]);
const failedProducts = ref<Product[]>([]);
const filters = ref<filterListType>({});
const activeFilters = ref<{ [key: string]: string }>({});
const lastChangedFilter = ref<string | null>(null)
const loading = ref(true);
const error = ref<string | null>(null);
const hash = ref("")
const possibleImagePositions = ref<string[]>([])
const fixImagePosition = ref<string>("")

const sliderProducts = computed(() => {
  const isPriceFilterActive = !!activeFilters.value.price
  const allItems = [...products.value, ...failedProducts.value].sort((a, b) => {
    if (isPriceFilterActive) {
      return (a.price ?? 0) - (b.price ?? 0)
    }
    return (a.order_idx ?? 0) - (b.order_idx ?? 0)
  })
  if (allItems.length === 0) return []
  const firstProduct = allItems[0]
  const base = firstProduct.image ? [{
    ...firstProduct.image,
    position: (firstProduct.image.filename.includes('video') || firstProduct.image.filename.endsWith('.mp4')) ? 'video' : 'main'
  }] : []
  const positions = firstProduct.image?.positions.map((p, idx) => {
    // try to match position name from filename if possible, otherwise use index
    let pos = `pos${idx + 2}`
    if (p.filename.includes('model')) pos = 'model'
    else if (p.filename.includes('closeup')) pos = 'closeup'
    else if (p.filename.includes('still')) pos = 'still'
    else if (p.filename.includes('video') || p.filename.endsWith('.mp4')) pos = 'video'
    else if (p.filename.includes('pos2')) pos = 'pos2'
    else if (p.filename.includes('pos3')) pos = 'pos3'
    else if (p.filename.includes('cross_sections')) pos = 'cross_sections'
    else if (p.filename.includes('single1')) pos = 'single1'
    else if (p.filename.includes('single2')) pos = 'single2'
    else if (p.filename.includes('set')) pos = 'set'

    return {...p, position: pos}
  }) ?? []

  // Ensure unique positions (sometimes the API might have duplicates or weird data)
  const all = [...base, ...positions]
  const seen = new Set()
  const unique = all.filter(item => {
    if (catalogItemType.value == "wedding_ring" && item.position == "closeup" && seen.has(item.position)) {
      item.position = "closeup-2"
    } else {
      if (seen.has(item.position)) return false
    }

    seen.add(item.position)
    return true
  })

  // Sort: primary 1st, pos* 3rd and 5th, middle images in between
  const first = unique[0]
  const isPrimary = first && (first.position === 'main' || first.position === 'video')

  const sorted = []
  if (isPrimary) sorted.push(first)

  const remaining = isPrimary ? unique.slice(1) : unique

  const posImages = remaining.filter(i => i.position.startsWith('pos'))
  const middleImages = remaining.filter(i => ['model', 'closeup', 'closeup-2', 'still'].includes(i.position))
  const rest = remaining.filter(i => !i.position.startsWith('pos') && !['model', 'closeup', 'closeup-2', 'still'].includes(i.position))

  let mIdx = 0
  let pIdx = 0

  // 2nd position: middle
  if (middleImages[mIdx]) sorted.push(middleImages[mIdx++])
  // 3rd position: pos
  if (posImages[pIdx]) sorted.push(posImages[pIdx++])
  // 4th position: middle
  if (middleImages[mIdx]) sorted.push(middleImages[mIdx++])
  // 5th position: pos
  if (posImages[pIdx]) sorted.push(posImages[pIdx++])

  // Remaining middle images
  while (mIdx < middleImages.length) sorted.push(middleImages[mIdx++])
  // Remaining pos images
  while (pIdx < posImages.length) sorted.push(posImages[pIdx++])
  // Everything else (video, etc.)
  sorted.push(...rest)

  return sorted
})


const updateUrlHash = () => {
  const newHash = "/" + Object.entries(activeFilters.value)
      .filter(([key, value]) => !(key === 'stone_type' && value === 'earth'))
      .map(([key, value]) => `${key}/${value}`)
      .join('/');

  if (newHash !== hash.value) {
    hash.value = newHash;
    const url = new URL(window.location.href);
    url.hash = hash.value;
    window.history.replaceState({}, '', url.href);
  }
};

const updateFilter = (filterName: string, value: string) => {

  if ((value === "" || (filterName === 'price_sort' && value === 'default')) && filterName !== 'stone_type') {
    removeFilter(filterName)
  } else {
    if (filterName === 'catalogItemType') {
      catalogItemType.value = value
      activeFilters.value = {
        stone_type: catalogItemType.value === 'engagement_ring_rings_of_the_stars' ? "lab" : "earth"
      }
    } else {
      activeFilters.value[filterName] = value === "" && filterName === 'stone_type' ? (catalogItemType.value === 'engagement_ring_rings_of_the_stars' ? "lab" : "earth") : value;

      if (filterName === 'price_sort' && value === 'max-stone-size' && activeFilters.value['price']) {
        activeFilters.value['stone_type'] = 'lab';
      }

      if (filterName === 'price_sort' && value === 'min-price') {
        activeFilters.value['stone_type'] = 'lab';
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete activeFilters.value['price'];
      }
    }

    activeFilters.value = filterLimiter.checkActiveFilters(activeFilters.value);

    lastChangedFilter.value = filterName
    updateUrlHash();
  }
};

const removeFilter = (filterName: string) => {
  if (filterName === 'catalogItemType') return

  if (filterName === 'stone_type') {
    activeFilters.value['stone_type'] = "earth"
  } else if (activeFilters.value[filterName]) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete activeFilters.value[filterName];
  }

  activeFilters.value = filterLimiter.checkActiveFilters(activeFilters.value);

  // Update lastChangedFilter reference
  if (lastChangedFilter.value === filterName) {
    const keys = Object.keys(activeFilters.value)
    lastChangedFilter.value = keys.length ? keys[0] : null
  }
  updateUrlHash();
};

const handleResetFilters = () => {
  activeFilters.value = {
    stone_type: catalogItemType.value === 'engagement_ring_rings_of_the_stars' ? "lab" : "earth"
  }
  lastChangedFilter.value = null
  updateUrlHash();
}

// Initialize active filters from the catalogFilters config (URL hash below takes precedence over these)
Object.entries(Config.getCatalogFilters()).forEach(([key, value]) => {
  if (key === 'catalogItemType') {
    catalogItemType.value = value
  } else {
    activeFilters.value[key] = value
  }
})

hash.value = window.location.hash.slice(1);
if (hash.value.startsWith('/')) {
  hash.value = hash.value.slice(1);
}

if (hash.value) {
  const filterPairs = hash.value.split('/');

  for (let i = 0; i < filterPairs.length; i += 2) {
    if (filterPairs[i] && filterPairs[i + 1]) {
      const idx: string = filterPairs[i] || ''
      activeFilters.value[idx.toString()] = filterPairs[i + 1] ? filterPairs[i + 1]?.toString() : ""
    }
  }
  if (activeFilters.value['catalogItemType']) {
    catalogItemType.value = activeFilters.value['catalogItemType']
    delete activeFilters.value['catalogItemType']
  }

  if (activeFilters.value['price_sort'] === 'max-stone-size' && activeFilters.value['price']) {
    activeFilters.value['stone_type'] = 'lab'
  }

  if (activeFilters.value['price_sort'] === 'min-price') {
    activeFilters.value['stone_type'] = 'lab'
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete activeFilters.value['price']
  }
}

if (!activeFilters.value['stone_type'] || activeFilters.value['stone_type'] === "") {
  activeFilters.value['stone_type'] = catalogItemType.value === 'engagement_ring_rings_of_the_stars' ? "lab" : "earth"
}

activeFilters.value = filterLimiter.checkActiveFilters(activeFilters.value);
updateUrlHash();

// Initialize lastChangedFilter if filters came from URL
if (!lastChangedFilter.value) {
  const keys = Object.keys(activeFilters.value)
  lastChangedFilter.value = keys.length ? keys[keys.length - 1] : null
}

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

const selectImagePosition = (position: string) => {
  fixImagePosition.value = position
}

const toggleFilters = () => {
  mobileFiltersOpen.value = !mobileFiltersOpen.value
}

// Build page title from ALL selected filters
// - order: alphabetically by localized value
// - values: localized using the same keys as catalogFilterValue component
// - separator: |
const numberFilter: Record<string, { decimal: number; unit: string }> = {
  stone_size: {decimal: 2, unit: 'ct.'}
}

const formatNumber = (filter: string, value: string | number): string => {
  const cfg = numberFilter[filter]
  if (!cfg) return String(value)
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  const rounded = Number(parseFloat(String(num)).toFixed(cfg.decimal))
  const localized = rounded.toLocaleString('de-DE')
  return cfg.unit ? `${localized} ${cfg.unit}` : localized
}

const selectedFilterValues = computed(() => {
  const entries = Object.entries(activeFilters.value)
  if (!entries.length) return [] as string[]

  const localizedValues = entries.map(([key, val]) => {
    // number formatting (same logic as catalogFilterValue)
    if (Object.prototype.hasOwnProperty.call(numberFilter, key)) {
      return formatNumber(key, val)
    }

    // translate value; fallback to raw when missing
    const translated = t(`components.catalog.filter.${key}.${val}`)
    return translated || String(val)
  })

  // Sort alphabetically (case-insensitive)
  return localizedValues.sort((a, b) => a.localeCompare(b, undefined, {sensitivity: 'base'}))
})

const title = computed(() => {
  const key = `components.catalog.catalogs.${catalogItemType.value}`
  const translated = t(key)
  return translated === key ? catalogItemType.value : translated
})


/**
 * Step Widget
 */

const showStepWidget = computed(() => {
  return ['engagement_ring', 'engagement_ring_rings_of_the_stars', 'necklace'].includes(catalogItemType.value)
})

</script>

<template>
  <div class="w-full flex flex-col items-center">
    <step-widget :show="showStepWidget" :catalog-id="catalogItemType">
      <div v-if="!isMobile && !loading && !error && sliderProducts.length > 0"
           class="w-full bg-white pt-4 pb-2 mb-4 overflow-hidden">
        <div class="px-6 mb-2">
          <h3 class="text-[9px] uppercase tracking-widest text-gray-900">
            {{ $t('components.catalog.imagePosition.title') }}
          </h3>
        </div>
        <div class="flex overflow-x-auto no-scrollbar px-6 gap-3">
          <div
              v-for="item in sliderProducts"
              :key="item.position"
              class="flex flex-col items-center flex-shrink-0 cursor-pointer"
              @click="selectImagePosition(item.position)"
          >
            <div
                class="w-10 h-10 rounded-full border p-0.5 transition-all flex items-center justify-center overflow-hidden"
                :class="fixImagePosition === item.position ? 'border-[#a99d8b] bg-[#a99d8b]/5' : 'border-gray-200'"
            >
              <element-video
                  v-if="item.filename.endsWith('.mp4')"
                  :src="item.filename"
                  video-class="w-full h-full object-contain rounded-full"
                  :autoplay="false"
                  :loop="false"
              />
              <div v-else class="w-full h-full relative">
                <img v-if="item.filename_layer" :src="item.filename_layer"
                     class="absolute top-0 left-0 w-full h-full object-contain rounded-full" :alt="item.position"/>
                <img :src="item.filename" class="w-full h-full object-contain rounded-full" :alt="item.position"/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </step-widget>
    <catalog-menu
        v-if="isMobile"
        v-model:mobile-filters-open="mobileFiltersOpen"
        :filters="mergedFilters"
        :active-filters="{...activeFilters, catalogItemType: catalogItemType}"
        :catalog-item-type="catalogItemType"
        @update-filter="updateFilter"
        @remove-filter="removeFilter"
        @reset-filters="handleResetFilters"
    />

    <!-- Mobile Image Selector Slider -->
    <div v-if="isMobile && !loading && !error && sliderProducts.length > 0"
         class="w-full bg-white pt-4 pb-2 border-b border-gray-100 mb-4 overflow-hidden">
      <div class="px-6 mb-4">
        <h3 class="text-xs font-bold uppercase tracking-widest text-gray-900">
          {{ $t('components.catalog.imagePosition.title') }}
        </h3>
      </div>
      <div class="flex overflow-x-auto no-scrollbar snap-x snap-proximity px-6 gap-6">
        <div
            v-for="item in sliderProducts"
            :key="item.position"
            class="flex flex-col items-center flex-shrink-0 snap-start"
            @click="selectImagePosition(item.position)"
        >
          <div
              class="w-20 h-20 rounded-full border p-1 mb-2 transition-all flex items-center justify-center overflow-hidden"
              :class="fixImagePosition === item.position ? 'border-[#a99d8b] bg-[#a99d8b]/5' : 'border-gray-200'"
          >
            <element-video
                v-if="item.filename.endsWith('.mp4')"
                :src="item.filename"
                video-class="w-full h-full object-contain rounded-full"
                :autoplay="false"
                :loop="false"
            />
            <div v-else class="w-full h-full relative">
              <img v-if="item.filename_layer" :src="item.filename_layer"
                   class="absolute top-0 left-0 w-full h-full object-contain rounded-full" :alt="item.position"/>
              <img :src="item.filename" class="w-full h-full object-contain rounded-full" :alt="item.position"/>
            </div>
          </div>
          <span
              class="text-[11px] uppercase tracking-wider text-center max-w-[80px]"
              :class="fixImagePosition === item.position ? 'text-[#a99d8b] font-bold' : 'text-gray-500'"
          >
            {{ $t(`components.catalog.imagePosition.${item.position}`) }}
          </span>
        </div>
      </div>
    </div>

    <div class="relative w-full flex justify-center m-0 p-0 lg:p-0 top-0 h-full  bg-white">
      <CatalogResult
          v-model:products="products"
          v-model:failedProducts="failedProducts"
          v-model:filters="filters"
          v-model:possibleImagePositions="possibleImagePositions"
          v-model:loading="loading"
          v-model:error="error"
          :catalog-brand="catalogBrand"
          :catalog-item-type="catalogItemType"
          :active-filters="activeFilters"
          :country="country"
          :language="language"
          :fix-image-position="fixImagePosition"
          :hash="hash"
          @update-filter="updateFilter"
      />
      <div v-if="!isMobile" class="w-1/4 pl-4 max-w-[400px] sticky top-20 h-fit">
        <div v-if="false" class="flex flex-col justify-center">
          <p
              class="flex-grow text-base tracking-tight text-gray-900 sm:text-2xl sm:pr-14 font-ivymode"
          >
            {{ title }}
          </p>
        </div>
        <CatalogMenuSkeleton v-if="loading"/>
        <catalog-menu
            :filters="mergedFilters"
            :active-filters="{...activeFilters, catalogItemType: catalogItemType}"
            :catalog-item-type="catalogItemType"
            @update-filter="updateFilter"
            @remove-filter="removeFilter"
            @reset-filters="handleResetFilters"
        />
      </div>
    </div>

  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>